import {
  Popover as MuiPopover,
  PopoverProps as MuiPopoverProps,
  useThemeProps,
} from '@mui/material';
import * as React from 'react';
import { useUnifyElevationProp } from 'src/styles/elevationUtils';

export interface PopoverOwnProps {
  /**
   * Unify system elevation level that determines shadow depth when `variant` is not set to `outlined`.
   * Accepts values between 0 and 6 inclusive.
   *
   * @default 3
   */
  elevation?: number;

  /**
   * If `true`, the component is shown.
   */
  open?: MuiPopoverProps['open'];
}

export type PopoverProps = Omit<MuiPopoverProps, 'elevation' | 'open'> &
  PopoverOwnProps;

/**
 * A Popover can be used to display some content on top of another.
 * Similar to a `Dialog`, except it remains affixed relative to its triggering element.
 *
 * Use a `PopoverTrigger` to control its stateful visibility.
 *
 * API:
 *
 * - [Popover API](https://mui.com/material-ui/api/popover/)
 * - inherits [Modal API](https://mui.com/material-ui/api/modal/)
 */
export const Popover = React.forwardRef<HTMLDivElement, PopoverProps>(
  function Popover(inProps, forwardedRef) {
    const props = useThemeProps({ props: inProps, name: 'MuiPopover' });
    const {
      children,
      elevation: unifyElevation = 3,
      open = false,
      ...rest
    } = props;

    const muiElevation = useUnifyElevationProp(unifyElevation);

    return (
      <MuiPopover
        {...rest}
        ref={forwardedRef}
        open={open}
        elevation={muiElevation}
      >
        {children}
      </MuiPopover>
    );
  }
);

export default Popover;
